<template>
  <AppEventContainer>
    <AppScreenTitle title="Your Events" />

    <!-- Tabs -->
    <AppTab :tabs="['Current', 'Past']" :selected-tab="activeView" @tab-selected="handleTabSelected" />

    <!-- Search Bar (only shown for current events) -->
    <div class="relative mb-4">
      <EventSearch @search-text-change="handleSearchTextChange" placeholder="Search current events"
        :show-filter-button="false" />
    </div>

    <!-- Loading State -->
    <AppLoading v-if="isLoading" :loading="isLoading"></AppLoading>

    <!-- Event Lists -->
    <div v-else-if="activeView === 'Current'">
      <div v-if="filteredCurrentEvents.length === 0" class="p-4 text-center">
        <AppEmptyState icon="far fa-calendar-days" :item-name="`current events`" />
      </div>
      <div v-else class="space-y-4">
        <template v-for="event in filteredCurrentEvents" :key="event.id">
          <!-- Use EventListItemHand for active events -->
          <EventListItemHand class="border-b border-border-light" v-if="isEventActive(event)" :event="event"
            @click="openItem(event)" />
          <!-- Use EventListItem for non-active events -->
          <EventListItem class="border-b border-border-light" v-else :poker-run-event="event"
            @click="openItem(event)" />
        </template>
      </div>
    </div>

    <div v-else-if="activeView === 'Past'">
      <div v-if="filteredPastEvents.length === 0" class="p-4 text-center">
        <AppEmptyState icon="far fa-calendar-days" :item-name="`past events`" />
      </div>
      <div v-else class="space-y-4">
        <EventListItem class="border-b border-border-light" v-for="event in filteredPastEvents" :key="event.id"
          :poker-run-event="event" @click="openItem(event)" />
      </div>
    </div>
  </AppEventContainer>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { PokerRunEvent } from "@models/PokerRunEvent";
import { useUserProfileStore } from "@/store/UserProfile";
import useEvent from "@/modules/useEvent";

import EventListItemHand from "@/components/EventListItemHand.vue";
import EventListItem from "@/components/EventListItem.vue";
import EventSearch from "@/components/EventSearch.vue";
import AppTab from "@/components/AppTab.vue";
import PokerRunEventDataService from "@/services/PokerRunEventDataService";
import AppLoading from "@/components/AppLoading.vue";
import { useEventParticipantStore } from "@/store/EventParticipant";
import { storeToRefs } from "pinia";



const { isEventActive, isPastEvent } = useEvent();
const userProfileStore = useUserProfileStore();
const { currentEventParticipants, isOffline } = storeToRefs(useEventParticipantStore());
const router = useRouter();

const activeView = ref<'Current' | 'Past'>('Current');
const searchText = ref('');
const currentEvents = ref<EnhancedPokerRunEvent[]>([]);
const pastEvents = ref<EnhancedPokerRunEvent[]>([]);
const isLoading = ref(true);

// Filtered events based on search
const filteredCurrentEvents = computed(() => {
  if (!searchText.value) return currentEvents.value;
  const search = searchText.value.toLowerCase();
  return currentEvents.value.filter(event =>
    event.name.toLowerCase().includes(search)
  );
});

const filteredPastEvents = computed(() => {
  if (!searchText.value) return pastEvents.value;
  const search = searchText.value.toLowerCase();
  return pastEvents.value.filter(event =>
    event.name.toLowerCase().includes(search)
  );
});

// Tab handling
const handleTabSelected = (tab: 'Current' | 'Past') => {
  activeView.value = tab;
};

// Search handling
const handleSearchTextChange = (text: string) => {
  searchText.value = text;
};

// Navigation
const openItem = (pokerRun: PokerRunEvent) => {
  router.push({ name: "RegisteredEvent", params: { id: pokerRun.id } });
};

// Initial data load
const loadEvents = async () => {
  isLoading.value = true;
  try {
    const items = await PokerRunEventDataService.getUserPokerRuns(userProfileStore.profile.id);

    // Split into current and past events
    currentEvents.value = items.filter((x) => {
      return !isPastEvent(x)
    });

    pastEvents.value = items.filter((x) => {
      return isPastEvent(x)
    });

    currentEvents.value.sort((a, b) => {
      return new Date(a.eventDate as string).getTime() - new Date(b.eventDate as string).getTime();
    });

    pastEvents.value.sort((a, b) => {
      return new Date(b.eventDate as string).getTime() - new Date(a.eventDate as string).getTime();
    });

  } catch (error) {
    console.error("Error loading events:", error);
  } finally {
    isLoading.value = false;
  }
};
watch(isOffline, (newValue) => {
  if (!newValue) {
    loadEvents();
  } else if (currentEventParticipants.value.length > 0) {
    router.replace({ name: "RegisteredEvent", params: { id: currentEventParticipants.value[0].eventId } });
  }
});
// Load events on component mount
onMounted(() => {
  if (isOffline.value && currentEventParticipants.value.length > 0) {
    router.replace({ name: "RegisteredEvent", params: { id: currentEventParticipants.value[0].eventId } });
  }
  loadEvents();
});
</script>
